package org.moxultimate.topwithmox.shows.ui

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.cards.ContentCard
import org.moxultimate.topwithmox.components.buttons.PressableButtonStyle
import org.moxultimate.topwithmox.components.chip.Chip
import org.moxultimate.topwithmox.components.chip.ChipRowScrollStyle
import org.moxultimate.topwithmox.components.error.ErrorPage
import org.moxultimate.topwithmox.components.loading.LoadingIndicator
import org.moxultimate.topwithmox.components.loading.LoadingMoreIndicator
import org.moxultimate.topwithmox.components.loading.OverlayLoadingIndicator
import org.moxultimate.topwithmox.components.searchbar.SearchBarWithAutocomplete
import org.moxultimate.topwithmox.components.texts.PageHeading
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.di.koinViewModel
import org.moxultimate.topwithmox.shows.data.ShowsResponse
import org.moxultimate.topwithmox.statics.ShowsStatics
import org.moxultimate.topwithmox.theme.*

/**
 * ShowsPage
 * Entry composable for the /shows route.
 * Observes [ShowsViewModel] state and delegates rendering to [ShowsListPage].
 */
@Composable
fun ShowsPage() {
    val viewModel: ShowsViewModel = koinViewModel()
    val screenState by viewModel.screenState.collectAsState()
    val isLoadingMore by viewModel.isLoadingMore.collectAsState()
    val hasMore by viewModel.hasMore.collectAsState()
    val allTypes by viewModel.allTypes.collectAsState()
    val allTags by viewModel.allTags.collectAsState()
    val selectedTypes by viewModel.selectedTypes.collectAsState()
    val searchText by viewModel.searchText.collectAsState()

    var lastSuccess by remember { mutableStateOf<ShowsState.Success?>(null) }
    if (screenState is ShowsState.Success) {
        lastSuccess = screenState as ShowsState.Success
    }

    val isFilterReloading = screenState is ShowsState.Loading && lastSuccess != null

    Box(
        Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .backgroundColor(WhiteBG)
            .overflow(Overflow.Hidden),
        contentAlignment = Alignment.Center,
    ) {
        when (val state = screenState) {
            is ShowsState.Loading -> {
                val cached = lastSuccess
                if (cached != null) {
                    ShowsListPage(
                        shows = cached.showsList,
                        allTypes = allTypes,
                        allTags = allTags,
                        selectedTypes = selectedTypes,
                        searchText = searchText,
                        isLoadingMore = isLoadingMore,
                        hasMore = hasMore,
                        onTypeToggle = { viewModel.toggleType(it) },
                        onSearchChange = { viewModel.setSearchText(it) },
                        onClearFilters = { viewModel.clearAllFilters() },
                        onLoadMore = { viewModel.loadMoreIfNeeded() },
                    )
                } else {
                    LoadingIndicator()
                }
            }

            is ShowsState.Error -> ErrorPage(
                errorCode = state.statusCode.toString(),
                error = state.error,
                onTryAgain = { viewModel.loadShows() },
            )

            is ShowsState.Success -> ShowsListPage(
                shows = state.showsList,
                allTypes = allTypes,
                allTags = allTags,
                selectedTypes = selectedTypes,
                searchText = searchText,
                isLoadingMore = isLoadingMore,
                hasMore = hasMore,
                onTypeToggle = { viewModel.toggleType(it) },
                onSearchChange = { viewModel.setSearchText(it) },
                onClearFilters = { viewModel.clearAllFilters() },
                onLoadMore = { viewModel.loadMoreIfNeeded() },
            )
        }

        OverlayLoadingIndicator(visible = isFilterReloading)
    }
}

/**
 * ShowsListPage
 * Full scrollable layout: page heading, search bar, type chips, then a 3-column grid of show cards.
 * Attaches an infinite-scroll listener that triggers [onLoadMore].
 *
 * @param shows Accumulated (and optionally filtered) list of shows.
 * @param allTypes Full type list for the scrollable chip row.
 * @param selectedTypes Currently active type filter chips.
 * @param searchText Live text filter applied to show title and description.
 * @param isLoadingMore True while the next pagination page is being fetched.
 * @param hasMore False when all pages have been loaded.
 * @param onTypeToggle Toggles a type chip on/off.
 * @param onSearchChange Updates [searchText] and re-filters the shows list.
 * @param onClearFilters Resets all chips and search text.
 * @param onLoadMore Triggered by the scroll handler to fetch the next page.
 */
@OptIn(DelicateApi::class)
@Composable
private fun ShowsListPage(
    shows: List<ShowsResponse>,
    allTypes: List<String>,
    allTags: List<String>,
    selectedTypes: Set<String>,
    searchText: String,
    isLoadingMore: Boolean,
    hasMore: Boolean,
    onTypeToggle: (String) -> Unit,
    onSearchChange: (String) -> Unit,
    onClearFilters: () -> Unit,
    onLoadMore: () -> Unit,
) {
    DisposableEffect(hasMore) {
        val handler: (org.w3c.dom.events.Event) -> Unit = {
            val scrolled = window.scrollY + window.innerHeight
            val total = window.document.documentElement?.scrollHeight ?: 0
            if (hasMore && scrolled >= total - 300) onLoadMore()
        }
        window.addEventListener("scroll", handler)
        onDispose { window.removeEventListener("scroll", handler) }
    }

    val breakpoint = rememberBreakpoint()
    val isMobile = breakpoint <= Breakpoint.SM
    val hasActiveFilters = selectedTypes.isNotEmpty() || searchText.isNotBlank()

    Column(
        modifier = if (isMobile)
            Modifier.fillMaxWidth().padding(leftRight = 16.px)
        else
            Modifier
                .width(clamp(min = 1200.px, value = 90.vw, max = 2000.px))
                .padding(leftRight = 3.cssRem),
    ) {
        PageHeading(
            subtitle = ShowsStatics.PAGE_SUBTITLE,
            titleLine1 = ShowsStatics.PAGE_TITLE_LINE1,
            titleLine2 = ShowsStatics.PAGE_TITLE_LINE2,
            description = ShowsStatics.PAGE_DESCRIPTION,
            detail = ShowsStatics.PAGE_DETAIL_1,
            detailType1 = ShowsStatics.PAGE_DETAIL_TYPE1,
            detail2 = ShowsStatics.PAGE_DETAIL_2,
            detailType2 = ShowsStatics.PAGE_DETAIL_TYPE2,
            detail3 = ShowsStatics.PAGE_DETAIL_3,
            detailType3 = ShowsStatics.PAGE_DETAIL_TYPE3,
        )

        Column(
            modifier = Modifier.fillMaxWidth().backgroundColor(Colors.White),
        ) {
            // Search bar row — search input + optional Clear Filters button
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.px),
            ) {
                Box(modifier = Modifier.flexGrow(1)) {
                    SearchBarWithAutocomplete(
                        value = searchText,
                        onValueChange = onSearchChange,
                        suggestions = allTags,
                        placeholder = ShowsStatics.SEARCH_PLACEHOLDER,
                        onSearch = onSearchChange,
                    )
                }

                if (hasActiveFilters) {
                    Row(
                        modifier = PressableButtonStyle.toModifier()
                            .padding(left = 14.px, top = 8.px, bottom = 6.px, right = 12.px)
                            .borderRadius(16.px)
                            .boxShadow(blurRadius = 2.px, color = Colors.LightGray)
                            .styleModifier { property("background", "#FFFFFF") }
                            .onClick { onClearFilters() },
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        SpanText(
                            text = "Clear Filters",
                            modifier = Modifier
                                .fontFamily(FontFamily)
                                .fontSize(clamp(min = MinChipFontSize, value = 0.8.vw, max = MaxChipFontSize))
                                .moxTextGradient(),
                        )
                    }
                }
            }

            // Type chips
            if (allTypes.isNotEmpty()) {
                Row(
                    modifier = ChipRowScrollStyle.toModifier()
                        .fillMaxWidth()
                        .padding(top = 12.px, bottom = 4.px),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.px),
                ) {
                    allTypes.forEach { type ->
                        Chip(
                            description = type,
                            isSelected = type in selectedTypes,
                            onChipClick = { onTypeToggle(type) },
                        )
                    }
                }
            }

        }

        // 3-column flex-wrap grid of show cards
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(topBottom = 30.px)
                .styleModifier {
                    property("flex-wrap", "wrap")
                    property("align-items", "stretch")
                },
            horizontalArrangement = Arrangement.spacedBy(24.px),
        ) {
            shows.forEach { show ->
                ShowCard(
                    show = show,
                    modifier = if (isMobile)
                        Modifier.fillMaxWidth().margin(bottom = 24.px)
                    else
                        Modifier
                            .styleModifier { property("width", "calc((100% - 48px) / 3)") }
                            .margin(bottom = 24.px),
                )
            }
        }

        if (isLoadingMore) {
            Box(
                modifier = Modifier.fillMaxWidth().padding(top = 12.px, bottom = 24.px),
                contentAlignment = Alignment.Center,
            ) {
                LoadingMoreIndicator()
            }
        }
    }
}

/**
 * ShowCard
 * Delegates to [ContentCard] using show-specific data and a "Watch Video" action button.
 *
 * @param show Show data to render.
 * @param modifier Outer modifier — caller controls width and margin for grid placement.
 */
@Composable
private fun ShowCard(
    show: ShowsResponse,
    modifier: Modifier = Modifier,
) {
    ContentCard(
        imageSrc = show.thumbnailPath.orEmpty(),
        type = show.type,
        title = show.title.orEmpty(),
        description = show.description,
        modifier = modifier,
        actions = {
            if (!show.videoLink.isNullOrBlank()) {
                GradientButton(
                    text = ShowsStatics.CARD_WATCH_VIDEO,
                    buttonHeight = 42,
                    onClick = { window.open(show.videoLink, "_blank") },
                )
            }
        },
    )
}
